'use client';

import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle2, Phone, Edit, Shield } from 'lucide-react';

interface OtpVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerificationSuccess: () => void;
  initialPhoneNumber: string;
  onPhoneNumberChange: (newPhone: string) => void;
  userName: string;
}

export default function OtpVerificationModal({
  isOpen,
  onClose,
  onVerificationSuccess,
  initialPhoneNumber,
  onPhoneNumberChange,
  userName
}: OtpVerificationModalProps) {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [isEditing, setIsEditing] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isVerified, setIsVerified] = useState(false);

  // Sync phone number when prop changes (render-phase adjust; body is pure setState)
  const [prevInitialPhone, setPrevInitialPhone] = useState<string | null>(null);
  if (initialPhoneNumber !== prevInitialPhone) {
    setPrevInitialPhone(initialPhoneNumber);
    // Extract only the last 10 digits from the phone number (remove country code)
    setPhoneNumber(initialPhoneNumber.replace(/^\+?91/, '').slice(-10));
  }

  // Reset state when modal closes (render-phase adjust on isOpen change; body is pure setState)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setOtp('');
      setOtpSent(false);
      setError('');
      setResendTimer(0);
      setIsVerified(false);
      setIsEditing(false);
    }
  }

  // Resend timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  if (!isOpen) return null;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(value);
    setError('');
  };

  const handleSavePhoneNumber = () => {
    if (phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    onPhoneNumberChange(phoneNumber);
    setIsEditing(false);
    setOtpSent(false);
    setOtp('');
    setError('');
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    // Additional validation to prevent invalid numbers
    const phoneValidation = validatePhoneNumber(phoneNumber);
    if (!phoneValidation.valid) {
      setError(phoneValidation.error || 'Invalid phone number');
      return;
    }

    setIsSendingOtp(true);
    setError('');

    try {
      const response = await fetch('/api/auth/whatsapp/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: `+91${phoneNumber}`,
          name: userName || 'User'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      setOtpSent(true);
      setResendTimer(60);
      setError('');
    } catch (error) {
      console.error('Send OTP error:', error);
      setError(error instanceof Error ? error.message : 'Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setError('');

    try {
      const response = await fetch('/api/auth/verify-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: `+91${phoneNumber}`,
          otp: otp
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify OTP');
      }

      setIsVerified(true);
      setTimeout(() => {
        onVerificationSuccess();
      }, 1500); // Show success message briefly before proceeding

    } catch (error) {
      console.error('Verify OTP error:', error);
      setError(error instanceof Error ? error.message : 'Failed to verify OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  // Phone number validation function
  const validatePhoneNumber = (phone: string) => {
    // Check if it's a valid Indian mobile number format
    if (!/^[6-9]\d{9}$/.test(phone)) {
      return { valid: false, error: 'Please enter a valid 10-digit Indian mobile number' };
    }
    
    // Check for common invalid patterns
    if (phone.startsWith('000') || phone.startsWith('999') || phone.startsWith('888')) {
      return { valid: false, error: 'This number pattern is not valid' };
    }
    
    // Check for sequential numbers
    if (/^(\d)\1{8}$/.test(phone)) {
      return { valid: false, error: 'This number pattern is not valid' };
    }
    
    return { valid: true };
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-zinc-200 max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 p-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20"></div>
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Verify Your Account</h2>
                <p className="text-blue-100 text-sm">खाता सत्यापन आवश्यक है</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isVerified}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isVerified ? (
            /* Success State */
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">Account Verified!</h3>
                <p className="text-green-600 text-sm">Proceeding to application confirmation...</p>
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info Message */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800 text-sm">Account Verification Required</h4>
                    <p className="text-blue-700 text-xs mt-1">
                      Before submitting your application, we need to verify your WhatsApp number with OTP.
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Number Section */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-800">
                  WhatsApp Number / व्हाट्सएप नंबर
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                                         <input
                       type="tel"
                       value={phoneNumber}
                       onChange={handlePhoneNumberChange}
                       disabled={!isEditing && otpSent}
                       className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-center font-mono text-lg text-gray-900 ${
                         isEditing ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-gray-50'
                       } ${(!isEditing && otpSent) ? 'opacity-60 cursor-not-allowed' : ''}`}
                       placeholder="Enter 10-digit number"
                       maxLength={10}
                     />
                  </div>
                  {!otpSent && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isEditing) {
                          handleSavePhoneNumber();
                        } else {
                          setIsEditing(true);
                        }
                      }}
                      className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      {isEditing ? 'Save' : 'Edit'}
                    </button>
                  )}
                </div>
              </div>

              {/* Send OTP Button */}
              {!otpSent && !isEditing && (
                <button
                  onClick={handleSendOtp}
                  disabled={isSendingOtp || phoneNumber.length !== 10}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isSendingOtp ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Phone className="h-4 w-4" />
                      Send OTP to WhatsApp
                    </>
                  )}
                </button>
              )}

              {/* OTP Input Section */}
              {otpSent && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-800 text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>OTP sent to +91{phoneNumber}. Check your WhatsApp messages.</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-black">
                      Enter 6-digit OTP
                    </label>
                                         <input
                       type="text"
                       placeholder="000000"
                       value={otp}
                       onChange={(e) => {
                         const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                         setOtp(value);
                         setError('');
                       }}
                       className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-center font-mono text-lg text-black font-bold"
                       maxLength={6}
                     />
                  </div>

                  <button
                    onClick={handleVerifyOtp}
                    disabled={isVerifyingOtp || otp.length !== 6}
                    className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                  >
                    {isVerifyingOtp ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Verifying OTP...
                      </div>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>

                  {/* Resend OTP */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Didn't receive OTP?</span>
                    <button
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || isSendingOtp}
                      className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendTimer > 0 ? (
                        `Resend in ${resendTimer}s`
                      ) : (
                        'Resend OTP'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
